import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Settings } from 'lucide-react';
import { AdditionalOption } from '../types';

interface AdditionalOptionHeaderProps {
  option: AdditionalOption;
  index: number;
  onUpdateOption: (id: string, updates: Partial<AdditionalOption>) => void;
  onRemoveOption: (id: string) => void;
}

export function AdditionalOptionHeader({
  option,
  index,
  onUpdateOption,
  onRemoveOption
}: AdditionalOptionHeaderProps) {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'select': return '📋';
      case 'checkbox': return '☑️';
      case 'text': return '📝';
      default: return '❓';
    }
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'select': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'checkbox': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'text': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="flex items-start justify-between gap-4 mb-4">
      <div className="flex-1 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-sm">
            {getTypeIcon(option.type)}
          </div>
          <Input
            value={option.label}
            onChange={(e) => onUpdateOption(option.id, { label: e.target.value })}
            className="flex-1 font-medium"
            placeholder="Option name..."
          />
          <Badge className={getTypeBadgeColor(option.type)}>
            {option.type}
          </Badge>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700">Type:</label>
            <Select
              value={option.type}
              onValueChange={(value: any) => onUpdateOption(option.id, { type: value })}
            >
              <SelectTrigger className="w-32 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="select">Dropdown</SelectItem>
                <SelectItem value="checkbox">Checkbox</SelectItem>
                <SelectItem value="text">Text Input</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700">Required:</label>
            <Switch
              checked={option.required}
              onCheckedChange={(checked) => onUpdateOption(option.id, { required: checked })}
            />
          </div>
        </div>
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={() => onRemoveOption(option.id)}
        className="h-8 px-3 text-red-600 hover:text-red-700 hover:border-red-300"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}
